import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckIcon, 
  XMarkIcon, 
  SparklesIcon,
  TrophyIcon 
} from '@heroicons/react/24/outline';

const ComparisonSection: React.FC = () => {
  const comparisonData = [
    {
      feature: 'ذكاء اصطناعي حقيقي',
      flyn: true,
      traditional: false,
      description: 'توصيات ذكية وتحليل تنبؤي متقدم'
    },
    {
      feature: 'شراكات تفعّل تلقائياً',
      flyn: true,
      traditional: false,
      description: 'خصومات تلقائية مع الشركاء المعتمدين'
    },
    {
      feature: 'دعم منصة مدد',
      flyn: true,
      traditional: false,
      description: 'تكامل مباشر مع نظام حماية الأجور'
    },
    {
      feature: 'تمويل مباشر مدمج',
      flyn: true,
      traditional: false,
      description: 'تقييم ائتماني وتمويل في 6 ساعات'
    },
    {
      feature: 'لوحة ديناميكية',
      flyn: true,
      traditional: false,
      description: 'تحديث لحظي للبيانات والتحليلات'
    },
    {
      feature: 'واجهة قابلة للتخصيص',
      flyn: true,
      traditional: false,
      description: 'تخصيص كامل حسب احتياجات الشركة'
    },
    {
      feature: 'تقارير أساسية',
      flyn: true,
      traditional: true,
      description: 'تقارير مالية تقليدية'
    },
    {
      feature: 'إدخال بيانات يدوي',
      flyn: false,
      traditional: true,
      description: 'عمل يدوي مكثف لإدخال البيانات'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section className="py-20 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full glass-card text-sm font-medium text-primary-600 dark:text-primary-400 mb-6">
            <TrophyIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
            مقارنة حقيقية
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            FLYN مقابل الحلول التقليدية
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            اكتشف لماذا تختار آلاف الشركات FLYN بدلاً من أنظمة المحاسبة التقليدية
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Comparison Table */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="glass-card rounded-2xl overflow-hidden shadow-2xl"
          >
            {/* Table Header */}
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-gray-700 dark:to-gray-600 p-6">
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                    الميزة
                  </h3>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 rtl:space-x-reverse">
                    <SparklesIcon className="w-6 h-6 text-primary-600" />
                    <h3 className="text-lg font-bold text-primary-600 dark:text-primary-400">
                      FLYN
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    الذكاء المالي المتطور
                  </p>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                    برنامج محاسبة تقليدي
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    الحلول التقليدية
                  </p>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {comparisonData.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                >
                  <div className="grid grid-cols-3 gap-6 items-center">
                    {/* Feature */}
                    <div className="text-right rtl:text-left">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {item.feature}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                    </div>

                    {/* FLYN */}
                    <div className="text-center">
                      {item.flyn ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="inline-flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full"
                        >
                          <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full"
                        >
                          <XMarkIcon className="w-5 h-5 text-gray-400" />
                        </motion.div>
                      )}
                    </div>

                    {/* Traditional */}
                    <div className="text-center">
                      {item.traditional ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="inline-flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full"
                        >
                          <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="inline-flex items-center justify-center w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full"
                        >
                          <XMarkIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
          >
            <div className="text-center glass-card p-6 rounded-xl">
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                85%
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                تقليل الوقت المطلوب للتحليل
              </div>
            </div>
            <div className="text-center glass-card p-6 rounded-xl">
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                60%
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                تحسن في دقة القرارات المالية
              </div>
            </div>
            <div className="text-center glass-card p-6 rounded-xl">
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                40%
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                توفير في التكاليف التشغيلية
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12"
          >
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                اتخذ قرارات مالية بثقة مع FLYN
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
                انضم إلى آلاف الشركات التي تحولت من الأنظمة التقليدية إلى الذكاء المالي المتطور
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-primary text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                ابدأ التجربة المجانية
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
